import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpResponse } from '@angular/common/http';
import { EMPTY, Observable, Subject, firstValueFrom, forkJoin, from, of, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

import { IFileInfo,
  RawFileInfo,
  ImageFile,
  FileMetadata,
  DisplayType,
  ImageInfo,
  FileSearchRequest
} from '../main/models/file-info';
import { FileInfoFactory } from '../main/models/file-info/file-info.factory';
import { TableData } from '../main/models/table-data';
import { ResultsSummary } from '../main/models/results';
import { ZoomRequest } from '../main/models/request';
import { MainState } from '../main/main.state';
import { MessageService, TreeNode } from 'primeng/api';
import { MainUtilities } from '../main/main.utilities';
import { catchError, expand, map, reduce, switchMap, tap, timeout } from 'rxjs/operators';
import { CacheProgressService } from './cache-progress.service';

// Cap on how many children are requested per page while listing a folder's
// siblings to detect a numbered image series. Pages are fetched repeatedly
// (see getAllChildren$) until a short page signals the end, so this only
// bounds request count, not the total series size.
const STACK_CHILDREN_PAGE_SIZE = 500;

// Matches a run of trailing digits at the end of a filename stem — the
// sequence index of a numbered image series (e.g. "image_007" -> "007").
const TRAILING_NUMBER_RE = /^(.*?)(\d+)$/;

// A sibling ROI geojson at least this large (bytes) is not downloaded: the viewer
// reads it from jit-service's region source (/regions/{id}/), the tiles in view at
// the zoom's level of detail. The viewer's own threshold for tiling a file in the
// browser (VizConfig.regionSourceMinChars) is the same 1,000,000.
export const REGION_SOURCE_MIN_BYTES = 1_000_000;

/** What jit-service's `/regions/detect` says about a file or folder. */
/**
 * Regions picked on the server for the Region Editor (sci-image-visualizer's `ServerRegionPick`,
 * mirrored until the release that declares it): files downloaded for it to import, or a region
 * set jit-service serves by level of detail, read without downloading it.
 */
export type ServerRegionPick =
  | { kind: 'files'; files: Array<{ name: string; data: ArrayBuffer | string }> }
  | { kind: 'source'; url: string; name?: string };

/** Region files read as text; anything else picked (a label volume, a zip, a zarr chunk) as bytes. */
const REGION_TEXT_PATTERN = /\.(geojson|json)$/i;
/** What makes a folder more than per-slice GeoJSON: a label volume, a zip, or zarr metadata (a store). */
const VOLUME_OR_ZIP_PATTERN = /\.(nii|nii\.gz|nrrd|zip)$/i;
const ZARR_METADATA_PATTERN = /^(\.zattrs|\.zgroup|\.zarray|zarr\.json)$/;
/** A label volume's ontology, picked with it from its folder. */
const ONTOLOGY_PATTERN = /^(regionset\.json|structure_graph[^/]*\.json)$/i;
/** The most files, and bytes, a folder walk hands over (a zarr store is one file per chunk). */
export const FOLDER_WALK_MAX_FILES = 20000;
export const FOLDER_WALK_MAX_BYTES = 2 * 1024 ** 3;

export interface RegionDetection {
  regions: boolean;
  /** The set id the `/regions/{id}/…` routes take. */
  id?: string;
  /** `file` (a .geojson) or `folder` (one .geojson per slice). */
  kind?: string;
  /** A folder's number of slice files. */
  slices?: number;
}

// Sibling folders a z-stack asks /regions/detect about, at most, when looking for its
// folder of per-slice region files (each costs the server a folder listing).
export const REGION_FOLDER_CANDIDATES = 20;
// A /regions/detect answer this late is not waited for: the stack opens without regions.
const REGION_DETECT_TIMEOUT_MS = 10_000;
/** How long the listing of a stack's folder may take before it opens without regions. */
const REGION_LIST_TIMEOUT_MS = 20_000;

/** A region-folder probe: jit-service's answer, or `failed` (an error or a timeout: unknown). */
type RegionProbe = { failed: false; detected: RegionDetection } | { failed: true };

/** A listed file's or folder's own name (a folder's may end in `/`). */
function leafName(raw: RawFileInfo): string {
  return (raw.name ?? '').replace(/\/+$/, '');
}

/**
 * "Everything but the trailing number" (name prefix + extension) — files
 * sharing this key belong to the same numbered series (e.g. "image_1.png"
 * and "image_2.png" share prefix "image_", extension "png"). Null when the
 * filename has no trailing number, so it can't be part of any series.
 */
function seriesKeyFor(file: ImageFile): { key: string; index: number } | null {
  const dot = file.name.lastIndexOf('.');
  const stem = dot > 0 ? file.name.substring(0, dot) : file.name;
  const extension = dot > 0 ? file.name.substring(dot + 1).toLowerCase() : '';
  const match = TRAILING_NUMBER_RE.exec(stem);
  if (!match) return null;
  return { key: `${match[1]} ${extension}`, index: parseInt(match[2], 10) };
}

/**
 * Finds the numbered image series (2+ members sharing a name prefix +
 * extension, e.g. "image_1.png".."image_5.png") that `target` belongs to
 * among its siblings, sorted by the numeric suffix. Returns null if `target`
 * has no trailing number, or no sibling shares its series — either way the
 * caller should fall back to loading `target` as a plain single image
 * instead of a bogus one-member "stack".
 */
function findSeriesContaining(siblings: ImageFile[], target: ImageFile): ImageFile[] | null {
  const targetKey = seriesKeyFor(target);
  if (!targetKey) return null;
  const matches: { file: ImageFile; index: number }[] = [];
  for (const file of siblings) {
    const key = seriesKeyFor(file);
    if (key && key.key === targetKey.key) {
      matches.push({ file, index: key.index });
    }
  }
  if (matches.length <= 1) return null;
  return matches
    .sort((a, b) => a.index - b.index || a.file.name.localeCompare(b.file.name))
    .map(candidate => candidate.file);
}

/** A detected numbered image series plus, for each member (aligned 1:1), its
 *  sibling `<stem>.geojson` raw info if one exists (null otherwise), and the
 *  index of the file the user actually clicked. */
interface SiblingSeries {
  files: ImageFile[];
  clickedIndex: number;
  geojsons: (RawFileInfo | null)[];
}

/**
 * One image-open request pushed onto {@link FilesService.imageOpenRequest$}.
 * Carries the file explicitly rather than re-reading `selectedFile` when the
 * response lands, so a load always completes against the file it started for.
 */
interface ImageOpenRequest {
  file: IFileInfo;
  asStack: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  private url: string;

  private selectedFile!: IFileInfo | undefined;
  private selectedNode!: TreeNode<IFileInfo> | null;
  /** One-shot flag: the next Diagram display request is a "Load as Stack"
   *  action (set by loadAsStack right before it selects the node, consumed by
   *  the very next selectDisplayType). */
  private pendingStackLoad = false;

  mainUtilities: MainUtilities = new MainUtilities();
  private rootNodes: TreeNode<IFileInfo>[] = [];
  /** relPaths of geojson ROIs saved during this session. The file tree isn't
   *  refreshed on save, so roiFileExist consults this in addition to the tree to
   *  keep the "overwrite existing file?" check correct without an HTTP probe. */
  private readonly savedRoiRelPaths = new Set<string>();
  /** Slice-files of the folder stack currently displayed, aligned by z-index
   *  (jit-ui#93). Set when a folder-of-images stack loads, cleared for a single
   *  file. A per-slice-file region save maps each slice's z to its file's
   *  sibling "<stem>.geojson" here. Undefined when the current image isn't a
   *  folder stack. */
  private currentStackFiles: ImageFile[] | undefined;

  /**
   * Image-open requests — one per {@link selectDisplayType} call, consumed by
   * the switchMap pipeline set up in the constructor.
   *
   * switchMap is what makes a second click CANCEL the first image's in-flight
   * `/metadata` request. Previously each click opened its own subscription, so
   * both requests ran to completion and whichever response landed LAST won
   * `setImageInfo$` — not whichever image the user actually clicked last.
   *
   * This replaces a pair of BehaviorSubjects (`fileMetadataSubject`,
   * `zoomOnRegionSubject`) that were switchMapped over but never `next`ed
   * anywhere in the codebase: they emitted their seed value once per
   * subscription and cancelled nothing, so the code read as if it debounced
   * image loads while doing the opposite.
   */
  private readonly imageOpenRequest$ = new Subject<ImageOpenRequest>();

  /**
   * File of the image whose `ImageInfo` was most recently published — i.e. the
   * one the viewer is being asked to render. Tile requests resolve their
   * identity from THIS, not from the tree selection (jit-ui#119).
   *
   * The tree selection is the wrong source of truth for tiles: it also moves
   * for things that don't load an image at all (a folder, a .csv, a .geojson),
   * and during an image switch it moves ahead of the render, so a tile source
   * built for image A could be handed image B's file. Set together with
   * `setImageInfo$` on every load path ({@link publisher}) so the two can't disagree.
   */
  private renderingFile: IFileInfo | undefined;
  /**
   * The file whose content is on screen: the image rendered ({@link renderingFile}), or what was
   * opened with no image of its own (a spatial dataset without a tissue image). The Region
   * Editor's server import browses its bucket. Kept apart from `renderingFile`, which owns tile
   * identity and must stay an image.
   */
  private shownFile: IFileInfo | undefined;

  /**
   * Bumped per image open. An image's publication can wait on requests of its own (its region
   * folder's discovery, a large ROI's detection, a ROI's content) that the switchMap below does
   * not cancel: each publishes through {@link publisher}, which drops it once a newer image was
   * opened, so an older image can never replace the newer one on screen.
   */
  private openGeneration = 0;
  /** Emits each time an image starts opening (see {@link imageOpened$}). */
  private readonly imageOpenedSubject = new Subject<void>();

  constructor(private http: HttpClient, private mainState: MainState, private messageService: MessageService,
              private cacheProgress: CacheProgressService) {
    this.url = environment.slideCropServer;
    mainState.getSelectedNode$().subscribe(
      (node) => {
        this.selectedNode = node;
        this.mainState.setSelectedFile(node?.data);
        this.selectedFile = node?.data;
      }
    );
    mainState.getFileInfo$().subscribe({
      next: fileInfo => {
        const nodes = this.mainUtilities.createNodes(fileInfo);
        if (this.rootNodes.length === 0) {
          this.mainState.setRootNodes(nodes);
        } else {
          this.selectedNode?.children?.push(...nodes);
        }
      },
    });
    mainState.getRootNodes$().subscribe(
      (rootNodes) => {
        this.rootNodes = rootNodes;
      }
    )

    // ONE long-lived pipeline for every image open. switchMap unsubscribes the
    // previous `/metadata` request when a newer click arrives, and HttpClient
    // turns that unsubscribe into an XHR abort — so the abandoned image can no
    // longer complete and overwrite the newer image's state.
    //
    // catchError sits INSIDE switchMap on purpose: an error surfacing on the
    // outer stream would terminate this subscription for the lifetime of the
    // service, leaving every subsequent click silently dead.
    this.imageOpenRequest$.pipe(
      switchMap(req =>
        this.http.get<FileMetadata>(this.getFileMetadataUrl(req.file.rawData)).pipe(
          map(metadata => ({ req, metadata })),
          catchError(err => {
            this.handleImageLoadError(err);
            return EMPTY;
          })
        )
      )
    ).subscribe(({ req, metadata }) => {
      if (req.asStack) {
        this.loadSelectedAsStack(req.file, metadata);
      } else {
        this.buildSingleFileImageInfo(req.file, metadata);
      }
    });
  }

  /**
   * Select display type. For Diagram (an image file), loads just that file —
   * a plain image, or its own server-side z-stack when the file itself has
   * z > 1 — exactly as it always has. Loading a numbered sibling series as one
   * stack is NOT automatic: it's the opt-in "Load as Stack" action, which
   * routes back through here with {@link pendingStackLoad} set (see
   * {@link loadAsStack}).
   */
  selectDisplayType(displayType: DisplayType | null) {
    if (displayType !== DisplayType.Diagram || !this.selectedFile) return;
    // Consume the one-shot "Load as Stack" intent (set by loadAsStack just
    // before it selected this node) so it applies to this display only.
    const asStack = this.pendingStackLoad;
    this.pendingStackLoad = false;
    this.openImage(this.selectedFile, asStack);
  }

  /**
   * Open an image that is not the tree selection — a spatial-omics bundle's
   * morphology image, opened when the bundle is (see SpatialOmicsService).
   */
  openImageFile(file: IFileInfo) {
    this.openImage(file, false);
  }

  private openImage(selectedFile: IFileInfo, asStack: boolean) {
    // set zoom to false as this is not a zoom event
    this.mainState.setZoom(false);
    this.mainState.setImageLoading(true);
    this.mainState.setImageLoadingMessage(asStack ? 'Loading image stack...' : 'Loading image...');
    // Start polling for cache-fill progress. The service auto-stops when
    // the backend reports complete, or after a brief idle window if no
    // fill is in progress for this file (already cached path).
    this.cacheProgress.start(selectedFile.rawData);
    this.openGeneration++;
    this.imageOpenedSubject.next();

    // Hand the request to the switchMap pipeline (see constructor) instead of
    // subscribing here. A per-click subscription is precisely what allowed two
    // image loads to run concurrently and race each other.
    this.imageOpenRequest$.next({ file: selectedFile, asStack });
  }

  /**
   * Explicit "Load as Stack" action (file-tree context menu). Loads the
   * numbered image series `node`'s image belongs to (e.g.
   * "image_1.png".."image_5.png") as one stack — opt-in, unlike a plain click
   * (single image).
   *
   * Routes through node selection (setSelectedNode) rather than loading
   * directly: selecting the node is what makes the FileViewer switch to the
   * Diagram view, so the visualization actually mounts and renders — and
   * clears the loading overlay — even when no image was open beforehand
   * (otherwise the overlay + cache-progress poll would hang). A one-shot
   * pendingStackLoad flag makes the resulting selectDisplayType load the
   * series as a stack instead of the single file, so there's no redundant
   * single-image load.
   */
  loadAsStack(node: TreeNode<IFileInfo>) {
    if (!(node?.data instanceof ImageFile)) return;
    this.pendingStackLoad = true;
    this.mainState.setSelectedNode(node);
  }

  /**
   * Series-load path for "Load as Stack" (reached from selectDisplayType when
   * pendingStackLoad was set). Guards a file that is ITSELF a multi-slice
   * volume (z > 1) and a file with no numbered sibling series back to a
   * single-image load, each with a toast; a rootless node (no parent to list)
   * also falls back to single.
   */
  private loadSelectedAsStack(file: IFileInfo, metadata: FileMetadata) {
    const generation = this.openGeneration;
    if (!(file instanceof ImageFile)) {
      this.buildSingleFileImageInfo(file, metadata);
      return;
    }
    const ownZ = metadata?.imageMeta?.[0]?.z ?? 1;
    if (ownZ !== 1) {
      // Each file is its own multi-slice volume — flattening the siblings into
      // one frame each would be wrong; load this file's own volume.
      this.messageService.add({ severity: 'info', summary: 'Already a volume',
        detail: `${file.name} is itself a multi-slice image; loaded as a single volume.` });
      this.buildSingleFileImageInfo(file, metadata);
      return;
    }
    const parentRaw = this.selectedNode?.parent?.data?.rawData;
    this.findSiblingSeries$(file, parentRaw).subscribe(series => {
      // A newer image was opened while the siblings were listed.
      if (generation !== this.openGeneration) return;
      if (series) {
        this.loadSeriesAsStack(series, metadata, this.selectedNode?.parent?.data?.name ?? file.name, generation);
      } else {
        this.messageService.add({ severity: 'warn', summary: 'No image series',
          detail: `No numbered image series found for ${file.name}; loaded as a single image.` });
        this.buildSingleFileImageInfo(file, metadata, generation);
      }
    });
  }

  /**
   * If `file` is a numbered image (e.g. "image_3.png") and 2+ siblings under
   * `parentRaw` share that same name prefix + extension, returns the full
   * series (sorted) plus `file`'s 0-based position within it. Fetches the
   * COMPLETE sibling listing (see getAllChildren$), not just whatever's
   * currently expanded in the tree, so a series longer than one tree page is
   * still detected correctly. Returns null — meaning "load as a plain single
   * image" — for a standalone file, an unrecognised naming pattern, a
   * rootless node (no parent folder to look in), or if the listing itself
   * errors; a sibling-lookup failure shouldn't block opening the image.
   */
  private findSiblingSeries$(
    file: ImageFile,
    parentRaw: RawFileInfo | undefined,
  ): Observable<SiblingSeries | null> {
    const clickedFile = file;
    if (!parentRaw) return of(null);
    return this.getAllChildren$(parentRaw).pipe(
      map(rawSiblings => {
        const siblings = rawSiblings
          .map(f => FileInfoFactory.getFileInfo(f))
          .filter((f): f is ImageFile => f instanceof ImageFile);
        const series = findSeriesContaining(siblings, clickedFile);
        if (!series) return null;
        const clickedIndex = series.findIndex(f => f.relPath === clickedFile.relPath);
        if (clickedIndex === -1) return null;
        // Match each slice-file's sibling "<stem>.geojson" (per-slice ROI, if
        // present) from the same folder listing. Aligned 1:1 with `series`;
        // null where a slice has no geojson. Keyed by name against the raw
        // listing (which includes non-image files like .geojson).
        const rawByName = new Map<string, RawFileInfo>();
        for (const raw of rawSiblings) rawByName.set(raw.name, raw);
        const geojsons = series.map(f => {
          const dot = f.name.lastIndexOf('.');
          const stem = dot > 0 ? f.name.substring(0, dot) : f.name;
          return rawByName.get(`${stem}.geojson`) ?? null;
        });
        return { files: series, clickedIndex, geojsons };
      }),
      catchError(err => {
        console.error('Error listing siblings to detect an image series; loading as a single image instead', err);
        return of(null);
      })
    );
  }

  // Build the stack ImageInfo from an already-fetched base metadata (the
  // clicked file's — every series member shares the same format/dimensions,
  // and it's a real z===1 slice, so it's a valid canonical base and saves a
  // second metadata round-trip). `folderName` labels the assembled stack.
  private loadSeriesAsStack(series: SiblingSeries, metadata: FileMetadata, folderName: string,
                            generation = this.openGeneration) {
    const files = series.files;
    // Remember the slice-files (aligned by z) so a per-slice-file region save
    // can write each slice back to its own sibling geojson (jit-ui#93).
    this.currentStackFiles = files;
    // Tile identity for a folder stack is the slice the user clicked — the
    // series members share format/dimensions, and this is the file whose
    // calibration/tiles are fetched (jit-ui#119). It takes it when published.
    const publish = this.publisher(generation, files[series.clickedIndex] ?? files[0]);
    const baseMeta = metadata.imageMeta[0];
    const imgInfo = new ImageInfo();
    imgInfo.trueImageSize = [baseMeta.x, baseMeta.y];
    imgInfo.isGrayscale = baseMeta.rgbChannels === 1;
    imgInfo.showStack = false;
    imgInfo.scaleRatio = true;
    imgInfo.fileName = folderName;
    imgInfo.isStack = true;
    imgInfo.imageMeta = [{ ...baseMeta, z: files.length }];
    imgInfo.urls = files.map(f => this.getPreviewUrl(f));
    imgInfo.smallUrls = files.map(f => this.getSmallPreviewUrl(f) as string);
    // Each slice is a DIFFERENT file's own preview — not one server-tiled
    // file with an internal z dimension (the single-file z-stack case,
    // which leaves this unset). tiled:false tells the OSD/napari backends
    // to fetch urls[z] directly per slice instead of assuming a single
    // fixed file + z tile parameter.
    imgInfo.tiled = false;
    // One-shot hint consumed by the visualization component: open on the
    // slice the user actually clicked, not slice 0.
    imgInfo.initialZIndex = series.clickedIndex;
    this.mainState.setImageCached(metadata.zoomStatus ? metadata.zoomStatus.cached : false);
    this.mainState.setFilename(imgInfo.fileName);

    // Physical pixel size (µm/px) for the scale bar: a folder stack renders via
    // the "simple" (tiled:false) path, which has no /tiles/info poll of its own,
    // and /metadata omits mppX for some formats (e.g. Micro-CT) — so the scale
    // bar would be blank. Fetch it once for the clicked slice (all series
    // members share the same calibration). Best-effort (jit-ui#93).
    const clickedRaw = series.files[series.clickedIndex]?.rawData;
    const calibration$ = clickedRaw ? this.fetchSliceCalibration$(clickedRaw) : of(null);

    // Per-slice ROIs: each slice-file may have its own sibling "<stem>.geojson"
    // that the viewer shows for that slice and swaps on scrub. Fetch the ones
    // that exist (a failed fetch degrades that slice to no-ROI, not the whole
    // stack); if no slice has a geojson, resolve to null and skip.
    const geojsons$ = series.geojsons.some(g => g != null)
      ? forkJoin(
          series.geojsons.map(raw =>
            raw ? this.getContent(raw).pipe(catchError(() => of(null))) : of(null as string | null),
          ),
        )
      : of(null as (string | null)[] | null);

    forkJoin({ calibration: calibration$, geojsons: geojsons$ }).subscribe(({ calibration, geojsons }) => {
      if (calibration && (calibration.mppX != null || calibration.mppY != null)) {
        const [m0, ...rest] = imgInfo.imageMeta;
        imgInfo.imageMeta = [{ ...m0, mppX: calibration.mppX, mppY: calibration.mppY }, ...rest];
      }
      if (geojsons) imgInfo.roiJsonStrs = geojsons;
      publish(imgInfo);
    });
  }

  /**
   * Fetch a slice's physical pixel size (µm/px) from GET /tiles/info for the
   * folder-stack scale bar (jit-ui#93). The "simple" render path has no
   * /tiles/info poll of its own, and /metadata omits mppX for some formats, so
   * the scale bar would otherwise be blank. Best-effort and non-blocking: 202
   * (still caching), a missing mppX, a timeout, or any error resolves to null
   * (no scale bar, exactly as before) instead of delaying/failing the stack load.
   */
  private fetchSliceCalibration$(rawData: RawFileInfo): Observable<{ mppX?: number; mppY?: number } | null> {
    const url = `${this.url}tiles/info?info=${this.encode(rawData)}`;
    return this.http.get<{ mppX?: number; mppY?: number }>(url, { observe: 'response' }).pipe(
      timeout(15000),
      map(resp => {
        const body = resp.status === 200 ? resp.body : null;
        return body && (body.mppX != null || body.mppY != null)
          ? { mppX: body.mppX ?? undefined, mppY: body.mppY ?? undefined }
          : null;
      }),
      catchError(() => of(null)),
    );
  }

  // Build the single-file ImageInfo from its already-fetched metadata (the
  // original per-file load path — either a plain image or a file that is its
  // own server-side z-stack).
  private buildSingleFileImageInfo(selectedFile: IFileInfo, metadata: FileMetadata,
                                   generation = this.openGeneration) {
    const publish = this.publisher(generation, selectedFile);
    const urls: any[] = [];
    // A bare `return` here left the "Loading image..." overlay spinning
    // forever with no error - the only thing that clears it on success is
    // the visualizer library rendering a tile, which never happens if we
    // bail before calling setImageInfo$. A 200 response with no/empty
    // imageMeta (server couldn't read dimensions for this file - seen for a
    // gzip-compressed TIFF) must surface the same way a failed /metadata
    // request already does via handleImageLoadError, not hang silently.
    if (!metadata || !metadata.imageMeta || metadata.imageMeta.length === 0) {
      this.handleImageLoadError(new Error(`No image metadata available for ${selectedFile.name}.`));
      return;
    }
    // Not a folder stack — drop any remembered slice-files so a region save
    // uses the single-file / combined path, not per-slice-file (jit-ui#93).
    this.currentStackFiles = undefined;
    // retrieve and set imageCached
    if (metadata.zoomStatus) {
      this.mainState.setImageCached(metadata.zoomStatus.cached);
    } else {
      this.mainState.setImageCached(false);
    }
    // set filename
    this.mainState.setFilename(metadata.fileName);
    const imageMeta = metadata.imageMeta[0];
    const imgInfo = new ImageInfo();
    imgInfo.trueImageSize = [ imageMeta.x, imageMeta.y ];
    imgInfo.isGrayscale = imageMeta.rgbChannels === 1;
    imgInfo.showStack = false;
    imgInfo.scaleRatio = true;
    imgInfo.fileName = metadata.fileName;
    imgInfo.isStack = imageMeta.z !== 1;
    imgInfo.imageMeta = metadata.imageMeta;

    // Build large-tier urls (the canonical ones the diagram
    // displays) and matching small-tier urls (used for the
    // fast first-paint placeholder; see ImageInfo.smallUrls).
    if (imageMeta.z === 1) {
      const previewUrl = this.getPreviewUrl(selectedFile);
      imgInfo.urls = [previewUrl];
      const smallUrl = this.getSmallPreviewUrl(selectedFile);
      if (smallUrl) imgInfo.smallUrls = [smallUrl];
    } else {
      // image is stack. Slice indices are 0-based on the server
      // (valid zIndex is 0..z-1), so iterate from 0 — a 1..z loop
      // skipped slice 0 and requested a phantom zIndex=z, which 400s
      // and aborted the whole stack load (empty heatmap/isosurface).
      const smallUrls: any[] = [];
      for (let i = 0; i < imageMeta.z; i++) {
        const url: string | undefined = this.getPreviewForSliceUrl(selectedFile, i);
        urls.push(url);
        const smallUrl = this.getSmallPreviewForSliceUrl(selectedFile, i);
        smallUrls.push(smallUrl);
      }
      imgInfo.urls = urls;
      imgInfo.smallUrls = smallUrls;
    }
    // Load a sibling "<image-stem>.geojson" ROI if one exists. The
    // sibling files are already in the loaded tree, so look there —
    // don't probe /api/metadata for the geojson, which 400s for any
    // non-image file (metadata can't be parsed) and was being used as a
    // bogus existence check that errored on every image without an ROI.
    const name = this.selectedNode?.data?.name as string | undefined;
    const stem = name ? name.substring(0, name.lastIndexOf('.')) : '';
    const geojsonNode = stem
      ? this.selectedNode?.parent?.children?.find(n => n.data?.name === `${stem}.geojson`)
      : undefined;
    if (geojsonNode?.data?.rawData) {
      this.loadRois(geojsonNode.data.rawData, imgInfo, publish);
    } else if (imgInfo.isStack && stem) {
      this.loadStackRegions(stem, imageMeta.z, imgInfo, publish);
    } else {
      publish(imgInfo);
    }
  }

  /**
   * Publish an image's `ImageInfo` for the open `generation`, unless a newer image was opened
   * since (see {@link openGeneration}). The file becomes the one the viewer renders, and so owns
   * tile identity (jit-ui#119), in the same step.
   */
  private publisher(generation: number, file: IFileInfo): (imgInfo: ImageInfo) => void {
    return (imgInfo) => {
      if (generation !== this.openGeneration) return;
      this.renderingFile = file;
      this.shownFile = file;
      this.mainState.setImageInfo$(imgInfo);
    };
  }

  /**
   * A z-stack's regions, from its folder's complete listing (every page, not the tree's loaded
   * one): its `<stem>.geojson`, else its folder of per-slice region files (see
   * {@link loadRegionFolder}). Without a listing (no parent, an error, a timeout) it opens
   * without regions.
   */
  private loadStackRegions(stem: string, slices: number, imgInfo: ImageInfo,
                           publish: (imgInfo: ImageInfo) => void): void {
    const parent = this.selectedNode?.parent?.data?.rawData as RawFileInfo | undefined;
    if (!parent) {
      publish(imgInfo);
      return;
    }
    this.getAllChildren$(parent).pipe(
      timeout(REGION_LIST_TIMEOUT_MS),
      catchError(() => of(null)),
    ).subscribe(children => {
      if (!children) {
        publish(imgInfo);
        return;
      }
      const raws = children as RawFileInfo[];
      const geojson = raws.find(raw => raw.type !== 'Folder' && leafName(raw) === `${stem}.geojson`);
      if (geojson) {
        this.loadRois(geojson, imgInfo, publish);
        return;
      }
      this.loadRegionFolder(stem, slices, raws.filter(raw => raw.type === 'Folder'), imgInfo, publish);
    });
  }

  /**
   * Publish a z-stack with the folder of per-slice region files beside it, as jit-service's
   * region source over every slice: the folder `<stem>.regions/`, else the one sibling folder
   * with as many `.geojson` files as the stack has slices (the Allen CCF's
   * `annotation_25_coronal/`, beside its 528-section volumes). `folders` is every sibling
   * folder (the complete listing).
   *
   * Without a named folder, a match is taken only when it is known to be the only one: every
   * sibling folder asked (no more than {@link REGION_FOLDER_CANDIDATES} of them), every answer
   * received, and exactly one matching. A probe that failed or timed out is unknown, not a
   * non-match, so then the stack opens without regions, as it does when several match.
   */
  private loadRegionFolder(stem: string, slices: number, folders: readonly RawFileInfo[], imgInfo: ImageInfo,
                           publish: (imgInfo: ImageInfo) => void): void {
    const named = folders.find(f => leafName(f) === `${stem}.regions`);
    // Too many to ask: whichever matched could not be known to be the only one.
    if (!named && folders.length > REGION_FOLDER_CANDIDATES) {
      publish(imgInfo);
      return;
    }
    const candidates = named ? [named] : folders;
    if (!candidates.length) {
      publish(imgInfo);
      return;
    }
    const detect = (folder: RawFileInfo): Observable<RegionProbe> =>
      this.http.get<RegionDetection>(`${this.url}regions/detect`, { params: { info: this.encode(folder) } }).pipe(
        timeout(REGION_DETECT_TIMEOUT_MS),
        map((detected): RegionProbe => ({ failed: false, detected })),
        // A jit-service without region sources, a folder it cannot list, a timeout: unknown.
        catchError(() => of<RegionProbe>({ failed: true })),
      );
    forkJoin(candidates.map(detect)).subscribe(probes => {
      if (!probes.some(p => p.failed)) {
        const sets = probes
          .map(p => (p.failed ? null : p.detected))
          .filter((d): d is RegionDetection =>
            !!d?.regions && d.kind === 'folder' && !!d.id && (!!named || d.slices === slices));
        if (sets.length === 1) imgInfo.regionSourceUrl = `${this.url}regions/${sets[0].id}/`;
      }
      publish(imgInfo);
    });
  }

  /**
   * Publish `imgInfo` with its sibling ROI geojson: a large one as jit-service's
   * region source (`regionSourceUrl`), so the viewer fetches only the tiles in view
   * and a feature's geometry when it is selected; a small one, or one jit-service
   * cannot serve as a source, as its text (`roiJsonStr`).
   */
  private loadRois(geojson: RawFileInfo, imgInfo: ImageInfo, publish: (imgInfo: ImageInfo) => void): void {
    const asText = () => this.getContent(geojson).subscribe({
      next: (content: string) => {
        imgInfo.roiJsonStr = content;
        publish(imgInfo);
      },
      error: (error) => {
        console.error(`error loading roi file content: ${error.message}`);
        publish(imgInfo);
      }
    });
    if ((geojson.size ?? 0) < REGION_SOURCE_MIN_BYTES) {
      asText();
      return;
    }
    this.http.get<RegionDetection>(`${this.url}regions/detect`, { params: { info: this.encode(geojson) } }).subscribe({
      next: (detected) => {
        if (detected.regions && detected.id) {
          imgInfo.regionSourceUrl = `${this.url}regions/${detected.id}/`;
          publish(imgInfo);
        } else {
          asText();
        }
      },
      // A jit-service without region sources: the file is read whole, as before.
      error: () => asText(),
    });
  }

  private handleImageLoadError(err: any) {
    const msg = err?.error?.message || err?.message || err?.statusText || String(err);
    console.error('Error occured', err);
    this.messageService.add({ key: 'center-toast', sticky: true, severity:'error', summary:'An error occured',
                        detail:`The following error occured: ${msg}.
                        Please try to open the image again through the file navigator.` });
    this.mainState.setLoadingError(true);
    this.mainState.setImageLoading(false);
    this.cacheProgress.stop();
  }

  // Fetches every page of a folder's children (see STACK_CHILDREN_PAGE_SIZE),
  // used by findSiblingSeries$ to see the complete sibling listing rather
  // than the tree's normal 50-per-page window, which would miss a series
  // longer than one page.
  /** Every child of `parent`, page by page until a short page ends the listing. */
  getAllChildren$(parent: RawFileInfo): Observable<any[]> {
    return this.getChildren(parent, 0, STACK_CHILDREN_PAGE_SIZE).pipe(
      expand((files, index) =>
        files.length === STACK_CHILDREN_PAGE_SIZE
          ? this.getChildren(parent, index + 1, STACK_CHILDREN_PAGE_SIZE)
          : EMPTY
      ),
      reduce((acc: any[], files) => acc.concat(files), [] as any[])
    );
  }

  /**
   * Check is geojson file for the given selectedFile exists in the same path
   * @param selectedFile
   * @return boolean
   */
  roiFileExist(selectedFile: IFileInfo | undefined, geojsonName?: string): Observable<boolean> {
    if (!selectedFile) return of(false);
    const dir = selectedFile.relPath.substring(0, selectedFile.relPath.lastIndexOf('/') + 1);
    const name = geojsonName || selectedFile.name.substring(0, selectedFile.name.lastIndexOf('.')) + '.geojson';
    // Look in the already-loaded sibling tree nodes (same folder) instead of probing
    // /api/metadata for the geojson, which 400s for a non-image file (metadata can't
    // be parsed). Also honour ROIs saved earlier this session, since the tree isn't
    // refreshed on save.
    const inTree = !!this.selectedNode?.parent?.children?.some(n => n.data?.name === name);
    return of(inTree || this.savedRoiRelPaths.has(dir + name));
  }

  getSelectedFileInfo(): IFileInfo | undefined {
    return this.selectedFile;
  }

  /**
   * File the viewer is currently rendering (the last `ImageInfo` published).
   * Prefer this over {@link getSelectedFileInfo} for anything that must agree
   * with the pixels on screen — tile fetches above all (jit-ui#119).
   *
   * Falls back to the selection before the first image of a session has loaded.
   * Note the full fix also needs the viewer library to render the newest
   * `ImageInfo` rather than dropping it (sci-image-visualizer#5); until that
   * lands, a dropped delivery still updates this, because the host cannot see
   * which render the library actually accepted.
   */
  getRenderingFileInfo(): IFileInfo | undefined {
    return this.renderingFile ?? this.selectedFile;
  }

  /**
   * The file whose content is on screen (see {@link shownFile}): the image rendered, or a file
   * shown with no image of its own; the selection before anything was shown.
   */
  getShownFileInfo(): IFileInfo | undefined {
    return this.shownFile ?? this.getRenderingFileInfo();
  }

  /** `file` is on screen with no image of its own (a spatial dataset without a tissue image). */
  setShownFile(file: IFileInfo): void {
    this.shownFile = file;
  }

  /**
   * How many images were opened so far: work started for one image (a region import resolving
   * files) compares it before landing, so it never lands on an image opened meanwhile.
   */
  getImageOpenGeneration(): number {
    return this.openGeneration;
  }

  /** Emits each time an image starts opening (another image, or the same one again). */
  imageOpened$(): Observable<void> {
    return this.imageOpenedSubject.asObservable();
  }

  getSelectedNode(): TreeNode<IFileInfo> | null {
    return this.selectedNode;
  }

  getRootNodes() {
    return this.rootNodes;
  }
  setRootNodes(rootNodes: TreeNode<IFileInfo>[]) {
    this.rootNodes = rootNodes;
  }

  isFileSelected$(): Observable<boolean> {
    return this.mainState.getSelectedFile$().pipe(
      map((info) => {
        return info != null;
      })
    );
  }

  public getProjects(): Observable<RawFileInfo[]> {
    return this.http.get<RawFileInfo[]>(this.url + 'projects');
  }

  public getChildren(parent: RawFileInfo, page: number = 0, size: number = 50): Observable<any[]> {
    const headers = { 'page': String(page), 'size': String(size) };
    return this.http.post<any[]>(`${this.url}children`, parent, { headers });
  }

  /**
   * Resolves a single file's full metadata directly from its storage URI
   * (gs://, s3://, or nio://) in one round trip - `GET /api/file-from-uri`
   * (jit-service FileController.fileFromUri / PathService.getFileInfo).
   * Used to open a Results-tab row without walking the file tree level by
   * level (see MainFacade.openResultPath) - the tree walk exists to reveal
   * a node in the visible tree, which the Results tab doesn't need.
   */
  public getFileInfoFromUri(uri: string): Observable<any> {
    return this.http.get<any>(`${this.url}file-from-uri`, { params: { uri } });
  }

  public createNodes(fileInfo: IFileInfo[]): TreeNode<IFileInfo>[] {
    return this.mainUtilities.createNodes(fileInfo);
  }

  public createLoadMoreNode(parentRawData: any, nextPage: number): TreeNode {
    return this.mainUtilities.createLoadMoreNode(parentRawData, nextPage);
  }

  // in some cases building the IFileInfo object is a horrific experience and not
  // worth the effort...so allow an unchecked input parameter for these cases
  public getHackedPreviewUrl(fileInfo: any): string {
    const base64: string = this.encode(fileInfo);
    return this.url + 'preview?info=' + base64;
  }

  public getPreviewUrl(file: IFileInfo | undefined): string {
    if (file) {
      const base64: string = this.encode(file.rawData);
      return this.url + 'preview?info=' + base64;
    }
    return this.url;
  }

  public getPreviewSizeUrl(file: IFileInfo): string {
    const base64: string = this.encode(file.rawData);
    return this.url + 'previewsize?info=' + base64;
  }

  public getPreviewForSliceUrl(file: IFileInfo | undefined, index: number): string | undefined {
    if (file) {
      const base64: string = this.encode(file.rawData);
      return `${this.url}preview?info=${base64}&zIndex=${index}`;
    }
    return undefined;
  }

  // Small-tier preview URLs request the same image at PREVIEW_SMALL_SIZE
  // (128 px by default). Used by the diagram component to paint a fast
  // blurry placeholder before the large tier finishes loading.
  public getSmallPreviewUrl(file: IFileInfo | undefined): string | undefined {
    if (file) {
      const base64: string = this.encode(file.rawData);
      return `${this.url}preview?info=${base64}&tier=small`;
    }
    return undefined;
  }

  public getSmallPreviewForSliceUrl(file: IFileInfo | undefined, index: number): string | undefined {
    if (file) {
      const base64: string = this.encode(file.rawData);
      return `${this.url}preview?info=${base64}&zIndex=${index}&tier=small`;
    }
    return undefined;
  }


  public getFileMetadataUrl(file: RawFileInfo): string {
    const base64: string = this.encode(file);
    return `${this.url}metadata?info=${base64}`;
  }

  /**
   * Plain metadata fetch. The image-open path no longer goes through here — it
   * uses the cancelling pipeline in the constructor (see
   * {@link imageOpenRequest$}) — so this is now just a thin HTTP wrapper for
   * callers that want a one-off read.
   */
  public getFileMetadata$(url: string): Observable<FileMetadata> {
    return this.http.get<FileMetadata>(url);
  }

  public getPreviewSize$(url: string): Observable<any> {
    return this.http.get<any>(url);
  }

  public getDownloadUrl(file: IFileInfo): string | null {
    if (file.rawData) {
      const base64: string = this.encode(file.rawData);
      return this.url + 'download?info=' + base64;
    } else {
      return null;
    }
  }

  // zoom requests
  public openZoomSession(file: IFileInfo) {
    const httpOptions: any = { headers: { 'Content-Type': 'application/json' }, responseType: 'text' };
    return this.http.post<any>(`${this.url}zoom/open-session`, file.rawData, httpOptions);
  }

  // The switchMap over `zoomOnRegionAction$` that used to wrap this was dead
  // weight for the same reason as the metadata one: nothing ever pushed to that
  // subject, so the seed value fired once per subscription and no request was
  // ever cancelled. Cancellation for a superseded zoom belongs to the caller's
  // subscription, so this is now the bare request it always effectively was.
  public zoomOnRegion(zoomRequest: ZoomRequest): Observable<any> {
    const httpOptions: any = { headers: { 'Content-Type': 'application/json', 'Accept': 'image/png' }, responseType: 'arraybuffer' };
    return this.http.post<any>(`${this.url}zoom/region`, zoomRequest, httpOptions);
  }

  public download(file: IFileInfo) {
    const url = this.getDownloadUrl(file);
    if (url) {
      return this.http.get(url, { responseType: 'blob' });
    } else {
      console.error('Could not find file info for download');
      throw new Error(`Failed to build URL for file info ${file.toString()}`);
    }
  }

  public encode(file: any): string {
    const json: string = JSON.stringify(file);
    return btoa(json);
  }

  /**
   * Stream search results from the backend as NDJSON.
   * Each emitted value is a raw file info object parsed from one line of the stream.
   * Unsubscribing cancels the in-flight HTTP request.
   */
  public search(request: FileSearchRequest): Observable<RawFileInfo> {
    return new Observable<RawFileInfo>(observer => {
      let processedLength = 0;

      const sub = this.http.post(
        `${this.url}search`,
        request,
        { responseType: 'text', observe: 'events', reportProgress: true }
      ).subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.DownloadProgress) {
            const partialText: string = event.partialText ?? '';
            const lastNewline = partialText.lastIndexOf('\n');
            if (lastNewline > processedLength) {
              const chunk = partialText.slice(processedLength, lastNewline + 1);
              processedLength = lastNewline + 1;
              for (const line of chunk.split('\n')) {
                const trimmed = line.trim();
                if (trimmed) {
                  try { observer.next(JSON.parse(trimmed)); }
                  catch (e) { console.error('Search NDJSON parse error:', trimmed, e); }
                }
              }
            }
          } else if (event.type === HttpEventType.Response) {
            const body: string = (event as HttpResponse<string>).body ?? '';
            const remaining = body.slice(processedLength);
            for (const line of remaining.split('\n')) {
              const trimmed = line.trim();
              if (trimmed) {
                try { observer.next(JSON.parse(trimmed)); }
                catch (e) { console.error('Search NDJSON parse error:', trimmed, e); }
              }
            }
            observer.complete();
          }
        },
        error: (err) => observer.error(err)
      });

      return () => sub.unsubscribe();
    });
  }

  public deleteFile(file: RawFileInfo): Observable<IFileInfo> {
    return this.http.post<IFileInfo>(`${this.url}delete`, file);
  }

  // in some cases building the IFileInfo object is a horrific experience and not
  // worth the effort...so allow an unchecked input parameter for these cases
  public getHackedContentUrl(fileInfo: any): string {
    const base64: string = this.encode(fileInfo);
    return this.url + 'content?info=' + base64;
  }

  /**
   * What the Region Editor imports from `picked` (entries chosen in the region picker):
   * - one folder: jit-service's region source over it when it serves the folder as a set, else its
   *   per-slice `.geojson` files (every page) and its `regionset.json`, downloaded;
   * - one GeoJSON file of {@link REGION_SOURCE_MIN_BYTES} or more: its region source, else its text;
   * - files (a label volume with its ontology JSON, GeoJSON files): downloaded, volumes as bytes.
   * Errors propagate: the editor reports them.
   */
  resolveRegionPick(picked: readonly RawFileInfo[]): Observable<ServerRegionPick | null> {
    if (!picked.length) return of(null);
    const [first] = picked;
    if (picked.length === 1 && first.type === 'Folder') {
      return this.detectRegionSet(first).pipe(
        switchMap((detected) =>
          detected?.regions && detected.kind === 'folder' && detected.id
            ? of(this.regionSourcePick(detected.id, first))
            : this.getAllChildren$(first).pipe(
                switchMap((children: RawFileInfo[]) => this.folderRegionFiles(first, children)),
                switchMap((files) => this.downloadRegionFiles(files)),
              ),
        ),
      );
    }
    if (picked.length === 1 && /\.geojson$/i.test(first.name) && (first.size ?? 0) >= REGION_SOURCE_MIN_BYTES) {
      return this.detectRegionSet(first).pipe(
        switchMap((detected) =>
          detected?.regions && detected.id
            ? of(this.regionSourcePick(detected.id, first))
            : this.downloadRegionFiles([first]),
        ),
      );
    }
    return this.downloadRegionFiles(picked.filter((p) => p.type !== 'Folder'));
  }

  /**
   * What a picked folder imports from its `children`: an OME-Zarr store's every file (its labels
   * are read from them), a label volume or zip with the ontology JSON beside it, else its
   * per-slice `.geojson` files and `regionset.json`.
   */
  private folderRegionFiles(folder: RawFileInfo, children: RawFileInfo[]): Observable<RawFileInfo[]> {
    const files = children.filter((c) => c.type !== 'Folder');
    if (files.some((c) => ZARR_METADATA_PATTERN.test(c.name))) return from(this.listFilesDeep(folder, children));
    if (files.some((c) => VOLUME_OR_ZIP_PATTERN.test(c.name))) {
      return of(files.filter((c) => VOLUME_OR_ZIP_PATTERN.test(c.name) || ONTOLOGY_PATTERN.test(c.name)));
    }
    return of(files.filter((c) => /(\.geojson|\/regionset\.json|^regionset\.json)$/i.test(c.relPath ?? c.name)));
  }

  /**
   * Every file under `folder`, breadth first (subfolders listed as they are reached; `listed`, the
   * folder's own children when they are already known). Refuses a tree of more than `maxFiles`
   * files or `maxBytes` bytes rather than download it.
   */
  async listFilesDeep(
    folder: RawFileInfo,
    listed?: RawFileInfo[],
    maxFiles = FOLDER_WALK_MAX_FILES,
    maxBytes = FOLDER_WALK_MAX_BYTES,
  ): Promise<RawFileInfo[]> {
    const files: RawFileInfo[] = [];
    const queue = [folder];
    let first = listed;
    let bytes = 0;
    while (queue.length) {
      const next = queue.shift() as RawFileInfo;
      const children: RawFileInfo[] = first ?? (await firstValueFrom(this.getAllChildren$(next)));
      first = undefined;
      for (const child of children) {
        if (child.type === 'Folder') {
          queue.push(child);
          continue;
        }
        files.push(child);
        bytes += child.size ?? 0;
        if (files.length > maxFiles) throw new Error(`${folder.name} holds more than ${maxFiles} files: choose a smaller folder`);
        if (bytes > maxBytes) {
          throw new Error(`${folder.name} holds more than ${(maxBytes / 1024 ** 3).toFixed(1)} GB: choose a smaller folder`);
        }
      }
    }
    return files;
  }

  /** jit-service's answer on whether `raw` is a region set it serves; null when it cannot tell. */
  private detectRegionSet(raw: RawFileInfo): Observable<RegionDetection | null> {
    return this.http
      .get<RegionDetection>(`${this.url}regions/detect`, { params: { info: this.encode(raw) } })
      .pipe(catchError(() => of(null)));
  }

  private regionSourcePick(id: string, raw: RawFileInfo): ServerRegionPick {
    return { kind: 'source', url: `${this.url}regions/${id}/`, name: raw.name };
  }

  /** `files` downloaded for the Region Editor: label volumes as bytes, the rest as text. */
  private downloadRegionFiles(files: readonly RawFileInfo[]): Observable<ServerRegionPick | null> {
    if (!files.length) return of(null);
    return forkJoin(
      files.map((f) =>
        (REGION_TEXT_PATTERN.test(f.name)
          ? (this.getContent(f) as Observable<ArrayBuffer | string>)
          : (this.getBytes(f) as Observable<ArrayBuffer | string>)
        ).pipe(
          map((data: ArrayBuffer | string) => ({ name: f.relPath ?? f.name, data })),
        ),
      ),
    ).pipe(map((downloaded): ServerRegionPick => ({ kind: 'files', files: downloaded })));
  }

  /** A file's bytes (`/download`), through HttpClient so the auth interceptor applies. */
  public getBytes(file: RawFileInfo): Observable<ArrayBuffer> {
    return this.http.get(`${this.url}download?info=${this.encode(file)}`, { responseType: 'arraybuffer' });
  }

  public getContentUrl(file: RawFileInfo): string {
    const base64: string = this.encode(file);
    return this.url + 'content?info=' + base64;
  }

  public getContent(file: RawFileInfo): Observable<string> {
    const url: string = this.getContentUrl(file);
    return this.http.get(url, { responseType: 'text' });
  }

  private getTableDataUrl(file: RawFileInfo): string {
    const base64: string = this.encode(file);
    return this.url + 'table?info=' + base64;
  }

  public getTableData(info: RawFileInfo): Observable<TableData> {
    const url: string = this.getTableDataUrl(info);
    return this.http.get<TableData>(url);
  }

  public getResultsSummary(info: RawFileInfo): Observable<ResultsSummary> {
    const base64: string = this.encode(info);
    const url: string = this.url + 'summary?info=' + base64;
    return this.http.get<ResultsSummary>(url);
  }

  public getHumanFileSize(info: ImageFile) {
    if (info == null) return '';
    if (info.type == 'Project') return '';
    if (info.size < 0) return '';
    return this.humanFileSize(info.size, true);
  }

  /**
   * Format bytes as human-readable text.
   *
   * @param bytes Number of bytes.
   * @param si True to use metric (SI) units, aka powers of 1000. False to use
   *           binary (IEC), aka powers of 1024.
   * @param dp Number of decimal places to display.
   *
   * @return Formatted string.
   */
  public humanFileSize(bytes: number, si = true, dp = 1): string {

    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
      return bytes + ' B';
    }

    const units = si
      ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
      : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    const r = 10 ** dp;

    do {
      bytes /= thresh;
      ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);


    return bytes.toFixed(dp) + ' ' + units[u];
  }

  /**
   * Persist GeoJSON content to the server alongside the currently selected image.
   * The file is saved as <image-stem>.geojson in the same storage location.
   */
  public saveGeoJson(content: string, geojsonName?: string): Observable<void> {
    if (!this.selectedFile) {
      throw new Error('No file is currently selected.');
    }
    const dir = this.selectedFile.relPath.substring(0, this.selectedFile.relPath.lastIndexOf('/') + 1);
    const name = geojsonName || this.selectedFile.name.substring(0, this.selectedFile.name.lastIndexOf('.')) + '.geojson';
    const rawFile = new RawFileInfo();
    rawFile.project = this.selectedFile.project;
    rawFile.relPath = dir + name;
    rawFile.type = this.selectedFile.type;
    rawFile.name = name;

    const info = this.encode(rawFile);
    return this.http.post<void>(`${this.url}save-content`, { info, content }, { responseType: 'text' as 'json' })
      // Remember it so roiFileExist reports it as existing even though the file
      // tree isn't reloaded after a save.
      .pipe(tap(() => this.savedRoiRelPaths.add(dir + name)));
  }

  /**
   * Save a folder stack's regions as one geojson per slice-file (jit-ui#93).
   * Each entry's `z` indexes into the current stack's slice-files (remembered in
   * {@link currentStackFiles} when the stack loaded); its geojson is written to
   * that slice-file's sibling "<stem>.geojson" in the same folder. Slices with
   * no matching file are skipped. Completes once every write succeeds.
   */
  public saveStackSliceGeoJsons(slices: { z: number; geoJsonStr: string }[]): Observable<void> {
    const files = this.currentStackFiles;
    if (!files) {
      // Return an error Observable (don't throw synchronously) so the failure
      // reaches the caller's .subscribe({ error }) like every other save path.
      return throwError(() => new Error('No image stack is currently loaded.'));
    }
    const saves = slices
      .filter(s => files[s.z])
      .map(s => {
        const f = files[s.z];
        const dir = f.relPath.substring(0, f.relPath.lastIndexOf('/') + 1);
        const dot = f.name.lastIndexOf('.');
        const stem = dot > 0 ? f.name.substring(0, dot) : f.name;
        const name = `${stem}.geojson`;
        const rawFile = new RawFileInfo();
        rawFile.project = f.project;
        rawFile.relPath = dir + name;
        rawFile.type = f.type;
        rawFile.name = name;
        const info = this.encode(rawFile);
        return this.http
          .post<void>(`${this.url}save-content`, { info, content: s.geoJsonStr }, { responseType: 'text' as 'json' })
          .pipe(tap(() => this.savedRoiRelPaths.add(dir + name)));
      });
    return saves.length ? forkJoin(saves).pipe(map(() => void 0)) : of(void 0);
  }
}
